class CartProgressBar extends HTMLElement {
  constructor() {
    super();
    this.goals = [];
    this.currentTotal = 0;
    this.maxValue = 0;
    this.percentages = [];
    this.cartUpdateUnsubscriber = undefined;
  }

  connectedCallback() {
    this.initializeGoals();
    this.render();
    this.subscribeToCartUpdates();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  initializeGoals() {
    // Parse goals from metaobject data attribute
    const goalsData = this.getAttribute('data-goals');
    if (goalsData) {
        this.goals = this.parseStringFormat(goalsData);
    }
    // Calculate max value for bar
    if (!this.goals || this.goals.length === 0) return;

    const values = this.goals.map(goal => parseFloat(goal.value) || 0);
    
    this.maxValue = Math.max(...values);

    // Calcular porcentajes con lógica especial para los primeros 500
    this.percentages = values.map(value => this.calculateGoalPercentage(value));

    // Get current cart total
    this.updateCartTotal();
  }

  calculateGoalPercentage(goalValue) {
    const firstThreshold = parseFloat(this.getAttribute('data-first-threshold')) || 0;
    const firstThresholdPercent = parseFloat(this.getAttribute('data-first-threshold-percent')) || 0;
    
    if (goalValue <= firstThreshold) {
      // Para valores <= firstThreshold, usar escala lineal hasta el firstThresholdPercent
      return (goalValue / firstThreshold) * firstThresholdPercent;
    } else {
      // Para valores > firstThreshold, usar la lógica actual pero ajustada
      // El resto de la barra (100% - firstThresholdPercent) se distribuye desde firstThreshold hasta maxValue * 1.15
      const remainingPercent = 100 - firstThresholdPercent; // X% restante de la barra
      const maxBarValue = this.maxValue * 1.15;
      const remainingValue = goalValue - firstThreshold;
      const remainingMaxValue = maxBarValue - firstThreshold;
      
      return firstThresholdPercent + (remainingValue / remainingMaxValue) * remainingPercent;
    }
  }

  parseStringFormat(data) {
    // Format: "value-label\nvalue-label\nvalue-label"
    // Example: "300-Envío Gratis\n700-Pastillero de Regalo\n1500-Proteina de Regalo"
    
    if (typeof data !== 'string') return [];
    
    // Try both \n (newline) and \\n (literal \n as string)
    const lines = data.includes('\n') ? data.split('\n') : data.split('\\n');
    
    return lines
      .filter(line => line.trim() !== '')
      .map(line => {
        const parts = line.split('***');
        if (parts.length >= 2) {
          const value = parts[0].trim();
          const label = parts[1].trim();
          const message = parts[2].trim();
          return { value, label, message };
        }
        return null;
      })
      .filter(goal => goal !== null);
  }

  updateCartTotal() {
    const oldTotal = this.currentTotal;
    
    // Try to get from attribute on the element itself first
    const totalAttr = this.getAttribute('data-cart-total');
    if (totalAttr) {
      this.currentTotal = (parseFloat(totalAttr) / 100 )|| 0;
    } else {
      // Fallback: calculate from cart JSON
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          this.currentTotal = (cart.total_price / 100) || 0;
          if (oldTotal !== this.currentTotal) {
            this.setAttribute('data-cart-total', this.currentTotal);
            this.render();
          }
        })
        .catch(err => {
          console.error('Error fetching cart total:', err);
          this.currentTotal = 0;
        });
      return; // Exit early to prevent render before fetch completes
    }
    
    // Only render if total changed
    if (oldTotal !== this.currentTotal) {
      this.render();
    }
  }

  subscribeToCartUpdates() {
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.updateCartTotal();
      });
    }
  }

  getCurrentProgress() {
    if (this.goals.length === 0) return 0;
    if (this.maxValue === 0) return 0; 
    
    // Usar la misma lógica que para calcular las posiciones de las metas
    const firstThreshold = parseFloat(this.getAttribute('data-first-threshold')) || 0;
    const firstThresholdPercent = parseFloat(this.getAttribute('data-first-threshold-percent')) || 0;
    if (this.currentTotal <= firstThreshold) {
      // Para valores <= firstThreshold, usar escala lineal hasta el firstThresholdPercent
      return Math.min((this.currentTotal / firstThreshold) * firstThresholdPercent, firstThresholdPercent);
    } else {
      // Para valores > firstThreshold, usar la lógica actual pero ajustada
      const remainingPercent = 100 - firstThresholdPercent; // X% restante de la barra
      const maxBarValue = this.maxValue * 1.15;
      const remainingValue = this.currentTotal - firstThreshold;
      const remainingMaxValue = maxBarValue - firstThreshold;
      
      const progressInRemainingSection = (remainingValue / remainingMaxValue) * remainingPercent;
      return Math.min(firstThresholdPercent + progressInRemainingSection, 100);
    }
  }

  getNextGoal() {
    for (let i = 0; i < this.goals.length; i++) {
      const goalValue = parseFloat(this.goals[i].value) || 0;
      if (this.currentTotal < goalValue) {
        return {
          goal: this.goals[i],
          index: i,
          remaining: goalValue - this.currentTotal
        };
      }
    }
    return null;
  }

  getCompletedGoals() {
    return this.goals.filter((goal, index) => {
      const goalValue = parseFloat(goal.value) || 0;
      return this.currentTotal >= goalValue;
    });
  }

  getMessage() {
    const nextGoal = this.getNextGoal();
    const completedGoals = this.getCompletedGoals();

    // If all goals completed
    if (completedGoals.length === this.goals.length) {
      return this.getAttribute('data-message-completed') || '¡Felicidades! Has desbloqueado todas las recompensas';
    }

    // Show message for next goal
    if (nextGoal) {
      const messageConnector = this.getAttribute('data-message-connector') || 'más para obtener';
      const remaining = nextGoal.remaining.toFixed(2);
      return `<span class="amount-goal">${this.formatCurrency(remaining)}</span>${messageConnector} ${nextGoal.goal.message}`;
    }

    return '';
  }

  formatCurrency(amount) {
    if (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) {
      const options = { style: 'currency', currency: Shopify.currency.active };
      return new Intl.NumberFormat('en-US', options).format(amount);
    }
    return `$${amount}`;
  }

  isGoalReached(index) {
    const goalValue = parseFloat(this.goals[index].value) || 0;
    return this.currentTotal >= goalValue;
  }

  render() {
    
    if (this.goals.length === 0) {
      this.innerHTML = '';
      return;
    }

    const progress = this.getCurrentProgress();
    const message = this.getMessage();

    let markersHTML = this.goals.map((goal, index) => {
      const isReached = this.isGoalReached(index);
      const percentage = this.percentages[index];
      const goalLabel = goal.label || '';
      
      return `
        <div class="progress-marker" style="left: ${percentage}%">
          <div class="marker-label">${goalLabel}</div>
          <div class="marker-circle ${isReached ? 'filled' : ''}">
          ${isReached ? '<svg xmlns="http://www.w3.org/2000/svg" width="800px" height="800px" viewBox="0 -1.5 14 14" id="meteor-icon-kit__solid-checkmark-xs" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M3.06066 4.9393C2.47487 4.3536 1.52513 4.3536 0.93934 4.9393C0.35355 5.5251 0.35355 6.4749 0.93934 7.0607L3.93934 10.0607C4.52513 10.6464 5.4749 10.6464 6.0607 10.0607L13.0607 3.0607C13.6464 2.47487 13.6464 1.52513 13.0607 0.93934C12.4749 0.35355 11.5251 0.35355 10.9393 0.93934L5 6.8787L3.06066 4.9393z" fill="#758CA3"/></svg>' : ''}
          </div>
        </div>
      `;
    }).join('');

    this.innerHTML = `
      <div class="progress-bar-container">
        <div class="progress-message" id="progress-message">${message}</div>
        <div class="progress-bar-track">
          <div class="progress-bar-fill" style="width: ${progress}%"></div>
          ${markersHTML}
        </div>
      </div>
    `;
  }

  static get observedAttributes() {
    return ['data-goals', 'data-cart-total'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      if (name === 'data-goals') {
        this.initializeGoals();
      } else if (name === 'data-cart-total') {
        this.currentTotal = parseFloat(newValue) || 0;
        this.render();
      }
    }
  }
}

customElements.define('cart-progress-bar', CartProgressBar);

